import { clsx } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { sideBarEntries } from './side-bar.config.tsx';
import SideBarItem from './side-bar-item';
import { Separator } from '@/components/ui/separator.tsx';
import { useGetApiVersionQuery } from '@/redux/queries/common.ts';
import { useTranslation } from 'react-i18next';
import PermissionControl from '@/components/PermissionControl.tsx';
import { UserRole } from '@/types/access.types.ts';
import SupportBox from './support-box';
import { useMediaQuery } from '@/lib/hooks.ts';
import { useUserPreferencesStore } from '@/store/user-preferences.store.ts';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import { Drawer, DrawerContent } from '@/components/ui/drawer.tsx';

interface ISidebar {
    isCollapsed: boolean;
    isMobile: boolean;
    onClick?: () => void;
}

const SidebarWrapper = () => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const sidebarCollapsed = useUserPreferencesStore((state) => state.sidebarCollapsed);
    const setSidebarCollapsed = useUserPreferencesStore((state) => state.setSidebarCollapsed);

    const handleClick = () => {
        if (isMobile) {
            setSidebarCollapsed(true);
        }
    };

    useEffect(() => {
        if (isMobile) {
            setSidebarCollapsed(true);
        } else {
            setSidebarCollapsed(false);
        }
    }, [isMobile, setSidebarCollapsed]);

    return isMobile ? (
        <Drawer open={!sidebarCollapsed} onClose={() => setSidebarCollapsed(true)}>
            <DrawerContent>
                <Sidebar
                    isCollapsed={sidebarCollapsed && !isMobile}
                    isMobile={isMobile}
                    onClick={handleClick}
                />
            </DrawerContent>
        </Drawer>
    ) : (
        <Sidebar isCollapsed={sidebarCollapsed} isMobile={isMobile} />
    );
};

const Sidebar = ({ isCollapsed, isMobile, onClick }: ISidebar) => {
    const [t] = useTranslation();
    const { data: apiVersion } = useGetApiVersionQuery();

    return (
        <div className="flex flex-col h-full text-foreground px-3">
            <div
                className={clsx('flex flex-col space-y-6 px-1')}
                data-dev-hint="optional div for having an extra footer navigation">
                <div className="h-16 relative flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <Link to="/" className="flex" title="Mevivo Energy Zähler">
                            {isCollapsed ? (
                                <img
                                    src="/assets/img/energy-logo-small.svg"
                                    className="w-6"
                                    alt=""
                                />
                            ) : (
                                <img
                                    src="/assets/img/energy-logo.svg"
                                    alt=""
                                    className={clsx('w-32')}
                                />
                            )}
                        </Link>
                    </div>

                    {isMobile && (
                        <button
                            id="mobile-menu-button"
                            type="button"
                            className={clsx('focus:outline-none rounded-md')}
                            onClick={() =>
                                useUserPreferencesStore.getState().setSidebarCollapsed(true)
                            }>
                            <X className="h-6 w-6" />
                        </button>
                    )}
                </div>
            </div>

            <nav className="flex flex-col flex-1 overflow-auto gap-2 mt-6">
                <div className={'flex flex-col gap-2 flex-grow'}>
                    {sideBarEntries.map((group, index) => (
                        <div key={`group-${index}`} className={'flex flex-col gap-2'}>
                            {index !== 0 && (
                                <PermissionControl access={[UserRole.Admin, UserRole.SuperAdmin]}>
                                    <Separator className="w-full" />
                                </PermissionControl>
                            )}
                            {group.entries.map((entry) => (
                                <PermissionControl key={entry.title} access={entry.access}>
                                    <SideBarItem
                                        menu={entry}
                                        isCollapsed={isCollapsed}
                                        onClick={onClick}
                                    />
                                </PermissionControl>
                            ))}
                        </div>
                    ))}
                </div>
            </nav>

            <div
                data-dev-hint="second-main-navigation or footer navigation"
                className="text-foreground flex flex-col justify-center my-4">
                <SupportBox isCollapsed={isCollapsed} />
                {!isCollapsed && (
                    <div className={'flex flex-col gap-2 mt-2'}>
                        <div className="text-xs text-slate-500 px-2 flex flex-row items-center gap-2">
                            <span>
                                {t('general.serverVersion', {
                                    version: apiVersion?.version ?? '-',
                                })}
                            </span>
                            <div className="w-1 h-1 rounded-full bg-slate-400" />
                            <span>
                                {t('general.webAppVersion', {
                                    version: import.meta.env.VITE_APP_VERSION ?? '-',
                                })}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SidebarWrapper;
